import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Badge } from '@/components/ui/badge';
import { Separator } from '@/components/ui/separator';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { DocumentPromptsService, DocumentPrompt } from '@/services/documentPromptsService';
import { useToast } from '@/hooks/use-toast';
import { Plus, Edit, Trash2, Star, StarOff, Copy } from 'lucide-react';

export function AdminPromptsManagement() {
  const [prompts, setPrompts] = useState<Record<string, DocumentPrompt[]>>({});
  const [selectedPrompt, setSelectedPrompt] = useState<DocumentPrompt | null>(null);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [loading, setLoading] = useState(true);
  const { toast } = useToast();

  const [formData, setFormData] = useState({
    document_type: '',
    prompt_name: '',
    system_prompt: '',
    user_prompt_template: '',
    is_active: true,
    is_default: false,
    metadata: {}
  });

  useEffect(() => {
    loadPrompts();
  }, []);

  const loadPrompts = async () => {
    try {
      const groupedPrompts = await DocumentPromptsService.getPromptsGroupedByType();
      setPrompts(groupedPrompts);
    } catch (error) {
      toast({
        title: "Error",
        description: "Failed to load prompts",
        variant: "destructive"
      });
    } finally {
      setLoading(false);
    }
  };

  const handleCreatePrompt = () => {
    setSelectedPrompt(null);
    setFormData({
      document_type: '',
      prompt_name: '',
      system_prompt: '',
      user_prompt_template: '',
      is_active: true,
      is_default: false,
      metadata: {}
    });
    setEditDialogOpen(true);
  };

  const handleEditPrompt = (prompt: DocumentPrompt) => {
    setSelectedPrompt(prompt);
    setFormData({
      document_type: prompt.document_type,
      prompt_name: prompt.prompt_name,
      system_prompt: prompt.system_prompt,
      user_prompt_template: prompt.user_prompt_template,
      is_active: prompt.is_active,
      is_default: prompt.is_default,
      metadata: prompt.metadata || {}
    });
    setEditDialogOpen(true);
  };

  const handleSavePrompt = async () => {
    try {
      if (selectedPrompt) {
        await DocumentPromptsService.updatePrompt(selectedPrompt.id, formData);
        toast({ title: "Success", description: "Prompt updated successfully" });
      } else {
        await DocumentPromptsService.createPrompt(formData);
        toast({ title: "Success", description: "Prompt created successfully" });
      }
      setEditDialogOpen(false);
      loadPrompts();
    } catch (error) {
      toast({
        title: "Error",
        description: "Failed to save prompt",
        variant: "destructive"
      });
    }
  };

  const handleDeletePrompt = async (promptId: string) => {
    if (!confirm('Are you sure you want to delete this prompt?')) return;
    
    try {
      await DocumentPromptsService.deletePrompt(promptId);
      toast({ title: "Success", description: "Prompt deleted successfully" });
      loadPrompts();
    } catch (error) {
      toast({
        title: "Error",
        description: "Failed to delete prompt",
        variant: "destructive"
      });
    }
  };

  const handleSetDefault = async (documentType: string, promptId: string) => {
    try {
      await DocumentPromptsService.setDefaultPrompt(documentType, promptId);
      toast({ title: "Success", description: "Default prompt updated" });
      loadPrompts();
    } catch (error) {
      toast({
        title: "Error",
        description: "Failed to update default prompt",
        variant: "destructive"
      });
    }
  };

  const handleClonePrompt = (prompt: DocumentPrompt) => {
    setSelectedPrompt(null);
    setFormData({
      document_type: prompt.document_type,
      prompt_name: `${prompt.prompt_name} (Copy)`,
      system_prompt: prompt.system_prompt,
      user_prompt_template: prompt.user_prompt_template,
      is_active: true,
      is_default: false,
      metadata: prompt.metadata || {}
    });
    setEditDialogOpen(true);
  };

  if (loading) {
    return <div className="p-6">Loading prompts...</div>;
  }

  return (
    <div className="p-6 max-w-7xl mx-auto">
      <div className="flex justify-between items-center mb-6">
        <div>
          <h1 className="text-3xl font-bold">Document Prompts Management</h1>
          <p className="text-muted-foreground">Manage AI prompts for different document types</p>
        </div>
        <Button onClick={handleCreatePrompt}>
          <Plus className="w-4 h-4 mr-2" />
          Create New Prompt
        </Button>
      </div>

      <div className="space-y-6">
        {DocumentPromptsService.getAvailableDocumentTypes().map(documentType => (
          <Card key={documentType}>
            <CardHeader>
              <CardTitle className="flex items-center justify-between">
                <span>{DocumentPromptsService.getDocumentTypeDisplayName(documentType)}</span>
                <Badge variant="outline">
                  {prompts[documentType]?.length || 0} prompt(s)
                </Badge>
              </CardTitle>
            </CardHeader>
            <CardContent>
              {prompts[documentType]?.length > 0 ? (
                <div className="space-y-4">
                  {prompts[documentType].map(prompt => (
                    <div key={prompt.id} className="border rounded-lg p-4">
                      <div className="flex items-start justify-between mb-2">
                        <div className="flex items-center gap-2">
                          <h4 className="font-medium">{prompt.prompt_name}</h4>
                          {prompt.is_default && (
                            <Badge variant="default" className="bg-yellow-100 text-yellow-800">
                              Default
                            </Badge>
                          )}
                          {!prompt.is_active && (
                            <Badge variant="outline" className="text-red-600">
                              Inactive
                            </Badge>
                          )}
                        </div>
                        <div className="flex items-center gap-2">
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => handleSetDefault(documentType, prompt.id)}
                            disabled={prompt.is_default}
                          >
                            {prompt.is_default ? (
                              <Star className="w-4 h-4 fill-current" />
                            ) : (
                              <StarOff className="w-4 h-4" />
                            )}
                          </Button>
                          <Button variant="ghost" size="sm" onClick={() => handleClonePrompt(prompt)}>
                            <Copy className="w-4 h-4" />
                          </Button>
                          <Button variant="ghost" size="sm" onClick={() => handleEditPrompt(prompt)}>
                            <Edit className="w-4 h-4" />
                          </Button>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => handleDeletePrompt(prompt.id)}
                            className="text-red-600 hover:text-red-700"
                          >
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        </div>
                      </div>
                      <p className="text-sm text-muted-foreground mb-2">
                        {prompt.system_prompt.slice(0, 150)}...
                      </p>
                      <div className="text-xs text-muted-foreground">
                        Created: {new Date(prompt.created_at).toLocaleDateString()}
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <p className="text-muted-foreground">No prompts available for this document type.</p>
              )}
            </CardContent>
          </Card>
        ))}
      </div>

      <Dialog open={editDialogOpen} onOpenChange={setEditDialogOpen}>
        <DialogContent className="max-w-4xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>
              {selectedPrompt ? 'Edit Prompt' : 'Create New Prompt'}
            </DialogTitle>
          </DialogHeader>
          
          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label htmlFor="document_type">Document Type</Label>
                <Select
                  value={formData.document_type}
                  onValueChange={(value) => setFormData(prev => ({ ...prev, document_type: value }))}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Select document type" />
                  </SelectTrigger>
                  <SelectContent>
                    {DocumentPromptsService.getAvailableDocumentTypes().map(type => (
                      <SelectItem key={type} value={type}>
                        {DocumentPromptsService.getDocumentTypeDisplayName(type)}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              
              <div>
                <Label htmlFor="prompt_name">Prompt Name</Label>
                <Input
                  id="prompt_name"
                  value={formData.prompt_name}
                  onChange={(e) => setFormData(prev => ({ ...prev, prompt_name: e.target.value }))}
                  placeholder="e.g., Executive Resume Prompt"
                />
              </div>
            </div>

            <div>
              <Label htmlFor="system_prompt">System Prompt</Label>
              <Textarea
                id="system_prompt"
                rows={6}
                value={formData.system_prompt}
                onChange={(e) => setFormData(prev => ({ ...prev, system_prompt: e.target.value }))}
                placeholder="Define the AI's role and expertise..."
              />
            </div>

            <div>
              <Label htmlFor="user_prompt_template">User Prompt Template</Label>
              <Textarea
                id="user_prompt_template"
                rows={8}
                value={formData.user_prompt_template}
                onChange={(e) => setFormData(prev => ({ ...prev, user_prompt_template: e.target.value }))}
                placeholder="Use variables like {resumeText}, {jobDescription}, {jobTitle}, {companyName}, {additionalInstructions}"
              />
              <p className="text-xs text-muted-foreground mt-1">
                Available variables: {'{resumeText}'}, {'{jobDescription}'}, {'{jobTitle}'}, {'{companyName}'}, {'{additionalInstructions}'}
              </p>
            </div>

            <div className="flex items-center gap-4">
              <label className="flex items-center gap-2">
                <input
                  type="checkbox"
                  checked={formData.is_active}
                  onChange={(e) => setFormData(prev => ({ ...prev, is_active: e.target.checked }))}
                />
                Active
              </label>
              <label className="flex items-center gap-2">
                <input
                  type="checkbox"
                  checked={formData.is_default}
                  onChange={(e) => setFormData(prev => ({ ...prev, is_default: e.target.checked }))}
                />
                Set as Default
              </label>
            </div>

            <div className="flex justify-end gap-2">
              <Button variant="outline" onClick={() => setEditDialogOpen(false)}>
                Cancel
              </Button>
              <Button onClick={handleSavePrompt}>
                {selectedPrompt ? 'Update' : 'Create'} Prompt
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}

export default AdminPromptsManagement;